

import { CalendarConfig } from "@/types/DocumentTemplate";
import { useTranslations } from "next-intl";
import {
  IconCalendar,
  IconMapPin,
  IconUser,
  IconBell,
  IconLink,
} from "@tabler/icons-react";

type Props = {
  config: CalendarConfig;
  currentLanguage: string;
  /**
   * IANA event timezone. The TZ-aware DatePicker emits a real UTC instant
   * (see DatePicker.emitSelectDate → wallPartsToIso), so the preview must
   * render that instant back in the event tz — exactly like the wizard/editor
   * inputs do via extractWallParts. Formatting in "UTC" here shifted the shown
   * time by the tz offset (e.g. 10:00 typed → 08:00 in the preview for CEST).
   */
  tz: string;
  /** Layout preset — see VARIANTS. Defaults to the 360px card. */
  variant?: CalendarPreviewVariant;
};

/**
 * Layout presets. The width is a CONTRACT, not styling.
 *
 * "fixed" (default) — the original 360px card. It MUST stay intrinsically
 *   sized: the send-mail modal scales it with FitBox
 *   (PSendEmail/components/AttachmentPreview.tsx:719), which measures
 *   `inner.scrollWidth` of an AUTO-width flex item. A percentage width there
 *   resolves against an indefinite containing block, is treated as `auto` for
 *   intrinsic sizing, and the card silently collapses to max-content (~200px
 *   for a sparse event). Never give this variant a % width.
 *
 * "compact" — fluid + tightened, for the ~318px document-wizard preview stage
 *   (WizardShell previewWidth lg:w-[360px] − WizardPreviewCard border − p-5).
 *   Its parent MUST be a plain block box, never a shrink-to-fit flex item, for
 *   the same reason. See DocumentTemplateWizard's calendar preview branches.
 *
 * Class strings are literals (never interpolated) so Tailwind's source scan
 * emits them.
 */
const VARIANTS = {
  fixed: { width: "w-[360px]", pad: "p-5", bodyGap: "gap-4", title: "text-lg" },
  compact: {
    width: "w-full max-w-[360px]",
    pad: "p-4",
    bodyGap: "gap-3",
    title: "text-base",
  },
} as const;

type CalendarPreviewVariant = keyof typeof VARIANTS;

/**
 * Status chip styling + label. House chip tokens rather than a raw Tailwind
 * palette (the previous `bg-green-100 dark:bg-green-900` pairs bypassed the
 * theme tokens entirely), and an EXPLICIT label map rather than
 * `t(status.toLowerCase())` — an unknown status must fall back to its raw
 * value, not throw inside next-intl. Keys are the ones the settings panel
 * already uses (CalendarSettingsPanel.tsx:118-120).
 */
const STATUS_CHIP: Record<string, string> = {
  CONFIRMED: "chip-green-bg chip-green-fg",
  TENTATIVE: "chip-yellow-bg chip-yellow-fg",
  CANCELLED: "chip-red-bg chip-red-fg",
};
const STATUS_LABEL_KEY: Record<string, string> = {
  CONFIRMED: "confirmed",
  TENTATIVE: "tentative",
  CANCELLED: "cancelled",
};

const formatDateTime = (
  iso: string,
  locale: string,
  allDay: boolean,
  tz: string,
): string => {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString(locale, {
      timeZone: tz,
      year: "numeric",
      month: "short",
      day: "numeric",
      ...(allDay ? {} : { hour: "2-digit", minute: "2-digit" }),
    });
  } catch {
    return iso;
  }
};

export function CalendarPreview({
  config,
  currentLanguage,
  tz,
  variant = "fixed",
}: Props) {
  const t = useTranslations("calendarBuilder");
  const v = VARIANTS[variant];
  const title = config.summary?.[currentLanguage] || t("eventTitleFallback");
  const location = config.location?.[currentLanguage];
  const description = config.description?.[currentLanguage];
  const allDay = !!config.allDay;

  return (
    <div
      className={`${v.width} rounded-2xl shadow-lg surface-primary overflow-hidden border outline-tertiary`}
    >
      {/* Header */}
      <div className={`${v.pad} cta-primary`}>
        <div className="flex items-center gap-3 mb-2">
          <IconCalendar size={24} className="text-white" />
          <span className="text-white text-xs font-medium uppercase tracking-wider">
            {t("calendarEvent")}
          </span>
        </div>
        {/* break-words: at the compact width the header content box is ~284px
            (~33 chars at text-base bold), and German compounds exceed that as
            a single unbreakable token — the root is overflow-hidden, so it
            would clip silently. */}
        <h2 className={`text-white ${v.title} font-bold break-words`}>{title}</h2>
        {config.status && (
          <span
            className={`inline-block mt-2 px-2 py-0.5 rounded text-xs font-medium ${
              STATUS_CHIP[config.status] || "surface-tertiary font-secondary"
            }`}
          >
            {STATUS_LABEL_KEY[config.status]
              ? t(STATUS_LABEL_KEY[config.status])
              : config.status}
          </span>
        )}
      </div>

      {/* Body */}
      <div className={`${v.pad} flex flex-col ${v.bodyGap}`}>
        {/* Date/Time */}
        <div className="flex items-start gap-3">
          <IconCalendar size={18} className="font-tertiary mt-0.5 shrink-0" />
          <div>
            <div className="body-2-medium font-primary">
              {config.startDate
                ? formatDateTime(config.startDate, currentLanguage, allDay, tz)
                : t("startDatePlaceholder")}
            </div>
            {config.endDate && (
              <div className="body-3-regular font-tertiary">
                — {formatDateTime(config.endDate, currentLanguage, allDay, tz)}
              </div>
            )}
            {allDay && (
              <span className="body-3-regular font-tertiary">({t("allDay")})</span>
            )}
          </div>
        </div>

        {/* Location */}
        {location && (
          <div className="flex items-start gap-3">
            <IconMapPin size={18} className="font-tertiary mt-0.5 shrink-0" />
            <div className="body-2-regular font-primary min-w-0 break-words">
              {location}
            </div>
          </div>
        )}

        {/* Description */}
        {description && (
          <div className="body-3-regular font-secondary border-t outline-tertiary pt-3 break-words">
            {description}
          </div>
        )}

        {/* URL */}
        {config.url && (
          <div className="flex items-center gap-3">
            <IconLink size={18} className="font-tertiary shrink-0" />
            {/* min-w-0 is what makes `truncate` work at all here: as a flex
                item the span's min-width:auto resolves to min-content, and
                truncate's nowrap makes min-content the WHOLE url — so it never
                shrank, the ellipsis never fired, and the url was clipped by
                the root's overflow-hidden instead. */}
            <span className="body-3-regular font-secondary truncate min-w-0">
              {config.url}
            </span>
          </div>
        )}

        {/* Organizer */}
        {(config.organizerName || config.organizerEmail) && (
          <div className="flex items-center gap-3">
            <IconUser size={18} className="font-tertiary shrink-0" />
            {/* min-w-0: a long organizer email has no break opportunity at "@"
                or "." and would overflow this flex item into the clipped root. */}
            <div className="min-w-0">
              {config.organizerName && (
                <div className="body-3-medium font-primary truncate">
                  {config.organizerName}
                </div>
              )}
              {config.organizerEmail && (
                <div className="body-3-regular font-tertiary truncate">
                  {config.organizerEmail}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Reminders */}
        {config.reminderMinutes && config.reminderMinutes.length > 0 && (
          <div className="flex items-start gap-3">
            <IconBell size={18} className="font-tertiary mt-0.5 shrink-0" />
            <div className="flex flex-wrap gap-1.5">
              {config.reminderMinutes.map((min, i) => (
                <span
                  key={i}
                  className="px-2 py-0.5 rounded surface-tertiary body-3-regular font-secondary"
                >
                  {min >= 1440
                    ? `${Math.floor(min / 1440)}d`
                    : min >= 60
                      ? `${Math.floor(min / 60)}h`
                      : `${min}m`}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
